import { defineStore } from 'pinia';
import { HEADERS } from 'src/enums';
import { inject, ref } from 'vue';
import { useQuasar } from 'quasar';

function getFormHeaders(headers) {
  return headers.reduce((accum, header) => {
    if (header.title) {
      if (header.type === HEADERS.H2) {
        accum.push({
          h2: header.title,
        });
      }
      if (header.type === HEADERS.H3) {
        if (accum.length) {
          if (accum[accum.length - 1].h3) {
            accum[accum.length - 1].h3.push(header.title);
          } else {
            accum[accum.length - 1].h3 = [header.title];
          }
        } else {
          accum.push({
            h3: [header.title],
          });
        }
      }
    }

    return accum;
  }, []);
}

export const useBlogStore = defineStore('blog', () => {
  const axios = inject('axios');
  const $q = useQuasar();

  const blog = ref(null);
  const blogs = ref([]);
  const posts = ref([]);
  const post = ref(null);
  const headers = ref([]);
  const postsTotal = ref(0);
  const internalPages = ref([]);
  const authors = ref([]);

  function getHeaders() {
    return post.value.headers?.reduce((accum, header) => {
      const arr = [];
      if (header.h2) {
        arr.push({
          type: HEADERS.H2,
          title: header.h2,
        });
      }

      if (header.h3?.length) {
        arr.push(...header.h3.map((h3) => ({ type: HEADERS.H3, title: h3 })));
      }

      return [...accum, ...arr];
    }, []);
  }

  async function getBlog(blogGuid) {
    const { data } = (await axios(`/generator/blogs/${blogGuid || blog.value.guid}`)).data;
    blog.value = data;
    $q.localStorage.set('blogGuid', data.guid);
  }

  async function generateSitemap() {
    const { data } = (await axios.put(`/generator/blogs/${blog.value.guid}/sitemap`)).data;
    blog.value.sitemap = data;
  }

  async function createBlog(form) {
    const { data } = (await axios.post('/generator/blogs', form)).data;
    blogs.value.push(data);
    return data;
  }

  async function deleteBlog(blogGuid) {
    await axios.delete(`/generator/blogs/${blogGuid}`);
    blogs.value = blogs.value.filter((item) => item.guid !== blogGuid);
  }

  async function enableBlog(blogGuid) {
    await axios.put(`/generator/blogs/${blogGuid}/enable`);

    const index = blogs.value.findIndex((item) => item.guid === blogGuid);
    if (index > -1) {
      blogs.value[index].isAvailable = true;
    }
  }

  async function getBlogs() {
    const { data } = (await axios('/generator/blogs')).data;
    blogs.value = data.elements;
    const blogGuid = $q.localStorage.getItem('blogGuid');
    const savedBlog = data.elements.find((item) => item.guid === blogGuid);

    if (savedBlog && savedBlog.isAvailable) {
      blog.value = savedBlog;
    } else {
      [blog.value] = data.elements.filter((item) => item.isAvailable);
    }

    await getBlog();
  }

  async function updateBlog(form) {
    const { data } = (await axios.put(`/generator/blogs/${blog.value.guid}`, form)).data;
    blog.value = data;

    const index = blogs.value.findIndex((item) => item.guid === blog.value.guid);
    if (index > -1) {
      blogs.value[index] = data;
    }
  }

  async function archivePost(postGuid) {
    await axios.delete(`/generator/blogs/${blog.value.guid}/posts/${postGuid}`);
  }

  async function getPosts(params = {}, lazy = false) {
    const { data } = (await axios(`/generator/blogs/${blog.value.guid}/posts`, {
      params,
    })).data;
    if (lazy) {
      posts.value = [...posts.value, ...data.elements];
    } else {
      posts.value = data.elements;
    }
    postsTotal.value = data.total;
  }

  async function toggleStatus(postGuid, status) {
    await axios.put(`/generator/blogs/${blog.value.guid}/posts/${postGuid}/status`, { status });
  }

  async function createPost(form) {
    const { data } = (await axios.post(`/generator/blogs/${blog.value.guid}/posts`, form)).data;
    post.value = data;
  }

  async function getPost(guid) {
    const { data } = (await axios.get(`/generator/blogs/${blog.value.guid}/posts/${guid}`)).data;
    post.value = data;
  }

  async function updatePost(title, immediatelyGenerateContent = false) {
    const form = {
      headers: getFormHeaders(headers.value),
      slug: post.value.slug,
      metaFields: post.value.metaFields,
      title,
    };
    const { data } = (await axios.put(`generator/blogs/${blog.value.guid}/posts/${post.value.guid}`, { ...form, immediatelyGenerateContent })).data;
    post.value = data;
  }

  async function refreshHeaders({ headersGenerationFields, metaFields }) {
    const { data } = (await axios.put(`generator/blogs/${blog.value.guid}/posts/${post.value.guid}/headers`, {
      headersGenerationFields,
      metaFields,
    })).data;
    post.value = data;
  }

  async function getInternalPages() {
    const { data } = (await axios.get(`generator/blogs/${blog.value.guid}/pages/internal`)).data;
    internalPages.value = data.elements;
  }

  async function saveInternalPage(form) {
    const { data } = (await axios.post(`generator/blogs/${blog.value.guid}/pages/internal`, form)).data;
    internalPages.value.push(data);
    return data;
  }

  async function updateInternalPage(form) {
    const { data } = (await axios.put(`generator/blogs/${blog.value.guid}/pages/internal/${form.guid}`, form)).data;

    const index = internalPages.value.findIndex((item) => item.guid === form.guid);
    if (index > -1) {
      internalPages.value[index] = data;
    }

    return data;
  }

  async function deleteInternalPage(internalPageGuid) {
    await axios.delete(`generator/blogs/${blog.value.guid}/pages/internal/${internalPageGuid}`);
    internalPages.value = internalPages.value.filter((item) => item.guid !== internalPageGuid);
  }

  async function getExternalResources() {
    const { data } = (await axios.get(`generator/blogs/${blog.value.guid}/external/resources`)).data;
    return data;
  }

  async function getExternalPostResources(postGuid) {
    const { data } = (await axios.get(`generator/blogs/${blog.value.guid}/posts/${postGuid}/resources`)).data;
    return data.elements;
  }

  async function syncronizePost(postGuid, integrationGuid) {
    const { data } = (
      await axios.put(`generator/blogs/${blog.value.guid}/posts/${postGuid}/resources/${integrationGuid}/sync`)
    ).data;
    return data;
  }

  async function getExternalRedirectUrl(postGuid, integrationGuid) {
    const { data } = (await axios.get(`generator/blogs/${blog.value.guid}/posts/${postGuid}/resources/${integrationGuid}/redirect`)).data;
    return data;
  }

  async function getAuthors() {
    const { data } = (await axios.get('generator/blogs/authors')).data;
    authors.value = data;
  }

  async function createAuthor(form) {
    const { data } = (await axios.post('generator/blogs/authors', form)).data;
    authors.value.push(data);
  }

  async function updateAuthor(form) {
    const { data } = (await axios.put(`generator/blogs/authors/${form.guid}`, form)).data;

    const index = authors.value.findIndex((item) => item.guid === form.guid);
    if (index > -1) {
      authors.value[index] = data;
    }
  }

  async function deleteAuthor(authorGuid) {
    await axios.delete(`generator/blogs/authors/${authorGuid}`);

    authors.value = authors.value.filter((item) => item.guid !== authorGuid);
  }

  async function addAuthorToPost(postGuid, authorGuid) {
    const { data } = (await axios.post(`generator/blogs/${blog.value.guid}/posts/${postGuid}/authors/${authorGuid}`)).data;
    return data;
  }

  async function deleteAuthorFromPost(postGuid, authorGuid) {
    await axios.delete(`generator/blogs/${blog.value.guid}/posts/${postGuid}/authors/${authorGuid}`);
  }

  return {
    blog,
    blogs,
    posts,
    getPosts,
    post,
    toggleStatus,
    getBlog,
    getBlogs,
    updateBlog,
    archivePost,
    createPost,
    getPost,
    updatePost,
    getHeaders,
    refreshHeaders,
    headers,
    postsTotal,
    getInternalPages,
    internalPages,
    saveInternalPage,
    updateInternalPage,
    deleteInternalPage,
    createBlog,
    deleteBlog,
    enableBlog,
    getExternalResources,
    getExternalPostResources,
    syncronizePost,
    getExternalRedirectUrl,
    getAuthors,
    createAuthor,
    updateAuthor,
    deleteAuthor,
    addAuthorToPost,
    deleteAuthorFromPost,
    generateSitemap,
    authors,
  };
});
